import { Button } from '@florencelabs/button';
import { Collapse } from '@florencelabs/collapse';
import { MaterialSymbol } from '@florencelabs/icons';
import { DateValue } from '@internationalized/date';
import clsx from 'clsx';

import Link from 'next/link';
import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAnalytics } from '@provider-directory/components/analytics/analytics-provider';
import { useOrgContext } from '@provider-directory/components/org-context/org-context-provider';
import { ProviderImage } from '@provider-directory/components/provider';
import { PROVIDER_SCHEDULING_TYPES } from '@provider-directory/constants';
import { CachedSlotsProvider } from '@provider-directory/contexts/CachedSlotsContext';
import { formatSpecialties } from '@provider-directory/functions/format-specialties';
import { getAvailablePhoneNumber } from '@provider-directory/functions/get-available-phone';
import { getSlug, getUrlSlug } from '@provider-directory/functions/get-slug';
import { joinProfessionalTitles } from '@provider-directory/functions/joinProfessionalTitles';
import type {
  ProviderResource,
  ProvidersSearchCriteria,
} from '@provider-directory/types/providers.contracts';
import { ProviderSortingEnum } from '@provider-directory/types/providers.contracts';

import { OutsideSearchAreaSeparator } from './outside-search-area-separator';
import { ProviderSearchCardDropdown } from './provider-card-dropdown';
import { ProviderCardGrid } from './provider-card-grid';
import { ProviderCardLocation } from './provider-card-location';
import { ProviderCardScheduling } from './provider-card-scheduling';

export interface ProviderSearchCardProps {
  provider: ProviderResource;
  testIdPrefix?: string | null;
  searchCriteria: ProvidersSearchCriteria;
  startDate?: DateValue | null;
  endDate?: DateValue | null;
  filterSpecialtyDisplayName?: string;
}

const formatProviderForCard = (
  provider: ProviderResource,
  searchCriteria: ProvidersSearchCriteria,
): ProviderResource => {
  if (
    searchCriteria.sort === ProviderSortingEnum.enum.availability ||
    !searchCriteria.sort
  ) {
    // show locations with the highest availability first when sorted by availability
    const locations = [...provider.locations].sort((a, b) => {
      // For radius searches, always keep in-range locations before out-of-range
      if (a.outsideSearchRadius && !b.outsideSearchRadius) return 1;
      if (!a.outsideSearchRadius && b.outsideSearchRadius) return -1;

      return b.availability - a.availability;
    });

    return { ...provider, locations };
  }

  return provider;
};

const getProviderDetailsUrl = (
  provider: ProviderResource,
  locationId: string | null,
): string => {
  const baseUrl = `/providers/${provider.npi}/${getSlug(provider.name.full)}`;
  if (locationId) {
    return `${baseUrl}?l=${getUrlSlug(locationId)}`;
  }

  return baseUrl;
};

const ProviderHeader = (props: {
  provider: ProviderResource;
  testIdPrefix?: string | null;
  featuredLocationId: string | null;
  onViewProfileClick: (href: string) => void;
}) => {
  const {
    provider,
    testIdPrefix = null,
    featuredLocationId,
    onViewProfileClick,
  } = props;
  const profileHref = getProviderDetailsUrl(provider, featuredLocationId);

  return (
    <div>
      <div className={'flex flex-col gap-6 pb-4 desktop:flex-row'}>
        <div className={'flex-1'}>
          <Link
            href={profileHref}
            onClick={() => {
              onViewProfileClick(profileHref);
            }}
          >
            <h3
              data-testid={
                testIdPrefix ? `${testIdPrefix}-provider-name` : null
              }
              className={
                'text-body-large-semibold desktop:text-body-xlarge desktop:font-semibold'
              }
            >
              {provider.name.full +
                joinProfessionalTitles(provider.professionalTitles)}
            </h3>
          </Link>

          {provider.specialties.length > 0 ? (
            <div
              className={
                'line-clamp-3 overflow-hidden text-ellipsis break-words text-body-small'
              }
            >
              {formatSpecialties(provider.specialties)}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const ProviderSearchCard = ({
  provider: unsortedProvider,
  startDate,
  endDate,
  searchCriteria,
  filterSpecialtyDisplayName,
}: ProviderSearchCardProps): ReactElement => {
  const { viewedProfileEvent } = useAnalytics();
  const orgContext = useOrgContext();
  const provider = formatProviderForCard(unsortedProvider, searchCriteria);
  const providerImage = provider.urls.find((url) => url.type === 'photo');
  const primaryLocation = provider.locations[0];
  const phoneNumber = getAvailablePhoneNumber(
    primaryLocation ?? null,
    provider,
    orgContext.getAssociationPhoneSelection(),
  );

  const handleViewProfileClick = (href: string): void => {
    viewedProfileEvent({
      provider,
      phoneNumber,
      linkContextPage: 'SearchResults',
      linkContextComponent: 'ProviderCard',
      searchCriteria,
      clickURL: href,
      filterSpecialtyDisplayName,
    });
  };

  // For radius searches, sort expand-section locations by distance asc.
  // We know it's a radius search if the backend set outsideSearchRadius on any location.
  const rawOtherLocations = provider.locations.slice(1);
  const isRadiusSearch = provider.locations.some(
    (loc) => loc.outsideSearchRadius === true,
  );
  const otherLocations = isRadiusSearch
    ? [...rawOtherLocations].sort(
        (a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity),
      )
    : rawOtherLocations;
  const inRangeLocations = otherLocations.filter(
    (loc) => !loc.outsideSearchRadius,
  );
  const outOfRangeLocations = otherLocations.filter(
    (loc) => loc.outsideSearchRadius,
  );
  const [showLocations, setShowLocations] = useState<boolean>(false);
  const toggleLocations = () => {
    setShowLocations(!showLocations);
  };

  const { t } = useTranslation();

  // Get the featured location (first location after sorting)
  const featuredLocationId = provider.locations[0]?.id;
  const profileHref = getProviderDetailsUrl(provider, featuredLocationId);

  return (
    <CachedSlotsProvider provider={provider} skipPrevSlots={true}>
      <div
        key={provider.npi}
        className={clsx(
          'mb-4 rounded-lg bg-white p-6 tablet:p-[32px] desktop:max-w-[49rem] desktop:p-6',
        )}
      >
        <ProviderCardGrid data-testid={`provider-${provider.npi}-search-card`}>
          <div
            className={clsx(
              'col-start-2 row-start-1',
              'tablet:row-span-2 desktop:col-start-1 desktop:row-span-2',
            )}
          >
            <Link
              href={profileHref}
              className={
                'block h-24 w-20 overflow-hidden rounded-lg tablet:h-[204px] tablet:w-[170px] desktop:h-[144px] desktop:w-[120px]'
              }
              onClick={() => {
                handleViewProfileClick(profileHref);
              }}
            >
              <ProviderImage
                testIdPrefix={'search-card'}
                name={provider.name}
                imageUrl={providerImage?.value}
                className={
                  'h-full w-full select-none object-cover transition-all duration-300 hover:scale-105 hover:cursor-pointer'
                }
                height={112}
                width={112}
              />
            </Link>
          </div>
          <div className={'row-start-1 h-min desktop:col-start-2'}>
            <ProviderHeader
              provider={provider}
              testIdPrefix={'search-card'}
              featuredLocationId={featuredLocationId}
              onViewProfileClick={handleViewProfileClick}
            />
          </div>
          {primaryLocation ? (
            <ProviderCardLocation
              location={primaryLocation}
              provider={provider}
              className={
                'col-span-full pt-6 tablet:col-span-1 tablet:pt-0 desktop:col-start-2'
              }
            />
          ) : null}
          {otherLocations.length > 0 ? (
            <Button
              variant={'clientLink'}
              className={
                'col-span-full col-start-1 mx-auto mt-[14px] flex items-center gap-x-2 py-[10px] !text-body-small-semibold leading-[140%] tracking-[-0.28px] text-primary-color desktop:col-span-1 desktop:col-start-2 desktop:mx-0 desktop:mt-[6px]'
              }
              onClick={toggleLocations}
              aria-expanded={showLocations}
              aria-controls={`locations-${provider.npi}`}
              testId={`toggle-locations-${provider.npi}`}
            >
              {showLocations
                ? t('details.hideLocations', {
                    count: provider.locations.length - 1,
                  })
                : t('details.viewMoreLocations', {
                    count: provider.locations.length - 1,
                  })}
              <MaterialSymbol
                name={'keyboard_arrow_up'}
                className={clsx('transition-transform duration-300', {
                  'rotate-180': !showLocations,
                })}
                aria-hidden={true}
              />
            </Button>
          ) : null}
          {primaryLocation &&
          provider.scheduling !== PROVIDER_SCHEDULING_TYPES.None ? (
            <div
              className={clsx(
                'col-span-full flex',
                'row-span-1 row-start-3 desktop:col-span-1 desktop:col-start-3 desktop:row-span-2 desktop:row-span-full desktop:row-start-1',
              )}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <ProviderCardScheduling
                provider={provider}
                location={primaryLocation}
                startDate={startDate}
                endDate={endDate}
                searchCriteria={searchCriteria}
              />
            </div>
          ) : null}
        </ProviderCardGrid>
        <Collapse open={showLocations} id={`locations-${provider.npi}`}>
          <div
            className={
              'mt-[14px] flex w-full flex-col gap-6 tablet:mt-[22px] tablet:gap-[32px] desktop:mt-[6px] desktop:gap-[16px]'
            }
          >
            {inRangeLocations.map((location) => (
              <ProviderSearchCardDropdown
                key={location.id}
                location={location}
                provider={provider}
                startDate={startDate}
                endDate={endDate}
                searchCriteria={searchCriteria}
              />
            ))}
            {outOfRangeLocations.length > 0 && <OutsideSearchAreaSeparator />}
            {outOfRangeLocations.map((location, i) => (
              <ProviderSearchCardDropdown
                key={location.id}
                location={location}
                provider={provider}
                startDate={startDate}
                endDate={endDate}
                searchCriteria={searchCriteria}
                showDivider={i !== 0}
              />
            ))}
          </div>
        </Collapse>
      </div>
    </CachedSlotsProvider>
  );
};

export default ProviderSearchCard;
